import Vue from 'vue';
import VueI18n from 'vue-i18n';
import { localize } from 'vee-validate';
import chinese from '@/locales/chinese.json';
import english from '@/locales/english.json';
import englishUs from '@/locales/english_us.json';
import french from '@/locales/french.json';
import german from '@/locales/german.json';
import italian from '@/locales/italian.json';
import portuguese from '@/locales/portuguese.json';
import russian from '@/locales/russian.json';
import spanish from '@/locales/spanish.json';
import chineseValidations from '@/locales/validations/chinese.json';
import englishValidations from '@/locales/validations/english.json';
import englishUsValidations from '@/locales/validations/english_us.json';
import frenchValidations from '@/locales/validations/french.json';
import germanValidations from '@/locales/validations/german.json';
import italianValidations from '@/locales/validations/italian.json';
import portugueseValidations from '@/locales/validations/portuguese.json';
import russianValidations from '@/locales/validations/russian.json';
import spanishValidations from '@/locales/validations/spanish.json';
import onboardingValidationMessages from '@/components/OnboardingAutomation/validationMsg.json';

Vue.use(VueI18n);

const localeMessages = {
    chinese,
    english,
    english_us: englishUs,
    french,
    german,
    italian,
    portuguese,
    russian,
    spanish
};

const localeValidationMessages = {
    chinese: chineseValidations,
    english: englishValidations,
    english_us: englishUsValidations,
    french: frenchValidations,
    german: germanValidations,
    italian: italianValidations,
    portuguese: portugueseValidations,
    russian: russianValidations,
    spanish: spanishValidations
};

const componentValidationMessages = {
    'OnboardingAutomation/validationMsg': onboardingValidationMessages
};

const i18n = new VueI18n({
    locale: 'english',
    fallbackLocale: 'english',
    messages: localeMessages
});

function setI18nLanguage(lang) {
    i18n.locale = lang;
    localize(lang, localeValidationMessages[lang] || englishValidations);
    return lang;
}

// Keep the Promise-based contract used by existing callers without creating a
// runtime Webpack chunk. A page opened before a deployment can therefore keep
// changing language after the release is activated.
export const loadLanguageAsync = function(lang) {
    const supportedLanguage = Object.prototype.hasOwnProperty.call(localeMessages, lang)
        ? lang
        : 'english';

    return Promise.resolve(setI18nLanguage(supportedLanguage));
};

export default i18n;

export const initializeLang = () => {
    return loadLanguageAsync(getLocalLanguage());
};

export const asyncLoadValidationMsgs = path => {
    const validationMessages = componentValidationMessages[path];

    if (!validationMessages) {
        return Promise.reject(new Error(`Unknown validation message set: ${path}`));
    }

    localize(i18n.locale, validationMessages);
    return Promise.resolve(validationMessages);
};

const normalizeLanguage = (rawLang) => {
    const lang = (rawLang || '').trim().toLowerCase().replace(/-/g, '_');
    const aliases = {
        english_us: 'english',
        en_us: 'english',
        en: 'english',
        de: 'german',
        germanic: 'german',
        fr: 'french',
        it: 'italian',
        es: 'spanish',
        ru: 'russian',
        zh: 'chinese',
        pt: 'portuguese',
        pt_pt: 'portuguese',
        pt_au: 'portuguese'
    };

    if (aliases[lang]) {
        return aliases[lang];
    }

    const baseLang = lang.split(/[-_]/)[0];
    return aliases[baseLang] || lang;
};

export const getLocalLanguage = () => {
    const availableLanguages = ['english', 'french', 'german', 'italian', 'spanish', 'russian', 'chinese', 'portuguese'];
    let initLang = (typeof window.wxlanguage === 'undefined') ? 'english' : atob(window.wxlanguage);
    initLang = normalizeLanguage(initLang);
    if (availableLanguages.indexOf(initLang) < 0) {
        initLang = 'english';
    }
    return initLang;
};
